import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BRAND } from '@/brand';
import '../styles/serviceSelect.css';

const LOGO_URL = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/Conncct-logo.png';

type Selection = 'setup' | 'investor';

const CheckIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>
);

function Signup() {
  const [selected, setSelected] = useState<Selection | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Referral attribution survives the brand redirect below (query params are dropped).
    const ref = searchParams.get('ref');
    if (ref) localStorage.setItem('signup_ref', ref);
    const service = searchParams.get('service');
    if (service === 'accounting') {
      localStorage.setItem('signupUserType', 'setup');
      localStorage.setItem('activeServiceView', 'accounting');
      ['plan', 'billing', 'flags', 'band', 'cur'].forEach(k => {
        const v = searchParams.get(k);
        if (v) localStorage.setItem(`signup_${k}`, v);
      });
      navigate('/accounting/select', { replace: true });
    } else if (service === 'consulting') {
      localStorage.setItem('signupUserType', 'investor');
      localStorage.setItem('activeServiceView', 'consulting');
      navigate('/consulting/signup', { replace: true });
    } else {
      // No explicit service: each domain is a single product, so route by brand
      // (corporate → accounting signup; consulting → consulting signup).
      navigate(BRAND === 'corporate' ? '/accounting/signup' : '/consulting/signup', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = () => {
    if (!selected) return;
    if (selected === 'setup') {
      localStorage.setItem('signupUserType', 'setup');
      navigate('/accounting/signup');
    } else {
      localStorage.setItem('signupUserType', 'investor');
      localStorage.setItem('activeServiceView', 'consulting');
      navigate('/consulting/signup');
    }
  };

  const pick = (val: Selection) => setSelected(val);

  return (
    <div className="ss-page">
      <div className="ss-dots" aria-hidden="true" />

      <nav className="ss-nav">
        <a href="/" className="ss-logo">
          <img src={LOGO_URL} alt="Conncct" style={{ height: 32, width: 'auto' }} />
        </a>
        <div className="ss-nav-right">
          <a className="ss-login" href="/login">Log in</a>
        </div>
      </nav>

      <div className="ss-wrap">
        <header className="ss-head">
          <span className="ss-eyebrow">Get started</span>
          <h1>How can we help you today?</h1>
          <p>Select the service that best fits your current business stage. We'll guide you through the rest.</p>
        </header>

        <div className="ss-grid" role="radiogroup" aria-label="Choose a service">

          {/* Setup & Growth — gold */}
          <div
            className={`ss-opt gold${selected === 'setup' ? ' sel' : ''}`}
            role="radio"
            aria-checked={selected === 'setup'}
            tabIndex={0}
            onClick={() => pick('setup')}
            onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); pick('setup'); } }}
          >
            <span className="ss-radio"><CheckIcon /></span>
            <div className="ss-tile">
              <svg viewBox="0 0 24 24">
                <path d="M4.5 16.5c-1.5 1.3-2 5-2 5s3.7-.5 5-2c.7-.8.7-2 0-2.9a2 2 0 0 0-3 0Z"/>
                <path d="M12 15 9 12c.6-3 2.4-6 7-7 1 4.6-2 6.4-5 7Z"/>
                <path d="M9 12H5s.5-2.2 2-3c1.7-.9 3 0 3 0"/>
                <path d="M12 15v4s2.2-.5 3-2c.9-1.7 0-3 0-3"/>
              </svg>
            </div>
            <div className="ss-kicker">For building</div>
            <h3>Setup &amp; Growth</h3>
            <p className="ss-lede">You're building the company. We build the foundation.</p>
            <ul className="ss-feats">
              <li><span className="ss-feat-ic"><CheckIcon /></span>Incorporated where it matters most: UAE, USA &amp; global hubs</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>Corporate Tax, VAT &amp; filings: fully managed</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>Clean books &amp; reporting, ready for any investor</li>
            </ul>
          </div>

          {/* Investor Readiness — blue */}
          <div
            className={`ss-opt${selected === 'investor' ? ' sel' : ''}`}
            role="radio"
            aria-checked={selected === 'investor'}
            tabIndex={0}
            onClick={() => pick('investor')}
            onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); pick('investor'); } }}
          >
            <span className="ss-radio"><CheckIcon /></span>
            <div className="ss-tile">
              <svg viewBox="0 0 24 24">
                <path d="M3 3v16a2 2 0 0 0 2 2h16"/>
                <path d="M7 14l4-4 3 3 5-6"/>
                <path d="M16 7h3v3"/>
              </svg>
            </div>
            <div className="ss-kicker">For raising</div>
            <h3>Investor Readiness</h3>
            <p className="ss-lede">You have a company. Now make it one investors can't ignore.</p>
            <ul className="ss-feats">
              <li><span className="ss-feat-ic"><CheckIcon /></span>Financials that hold up under VC scrutiny</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>A pitch deck built to get second meetings</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>All documents investors want: ready before they ask</li>
            </ul>
          </div>

        </div>

        <div className="ss-foot">
          <button className="ss-back" onClick={() => navigate(-1)}>← Back</button>
          <div className="ss-cont-wrap">
            {!selected && <span className="ss-hint">Select an option to continue</span>}
            <button className="ss-continue" disabled={!selected} onClick={handleContinue}>
              {selected === 'setup'
                ? 'Continue with Setup & Growth →'
                : selected === 'investor'
                ? 'Continue with Investor Readiness →'
                : 'Continue →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
